import AndroidIcon from '@mui/icons-material/Android';
import AppleIcon from '@mui/icons-material/Apple';
import QrCodeScannerIcon from '@mui/icons-material/QrCodeScanner';
import { Avatar, Button, Divider, List, ListItem, Modal, Paper, Stack, Typography } from '@mui/material';
import { isTauri } from '@tauri-apps/api/core';
import QRCode from 'qrcode.react';
import { useState } from 'react';

import getCDNImagePath from '../../Utils/getCDNImagePath';
import DeviceIcons from '../Icons/DeviceIcons';

interface QRCodeModalProps {
    handleClose: () => void;
    open: boolean;
    update?: boolean;
    url?: string;
}

export default function QRCodeModal({ handleClose, open, update, url }: Readonly<QRCodeModalProps>) {
    const IsTauri = isTauri();

    const [downloadApp, setDownloadApp] = useState(false);

    const handleDisplayDownloadApp = () => {
        setDownloadApp((prev) => !prev);
    };

    const renderQrContent = () => {
        switch (true) {
            case !IsTauri:
                return <DownloadAppPc />;
            case !downloadApp:
                return <DownloadAppMobile handleDisplayRedirectQr={handleDisplayDownloadApp} url={url} />;
            default:
                return <QrRedirect handleDisplayRedirectQr={handleDisplayDownloadApp} update={update} url={url} />;
        }
    };

    return (
        <Modal onClose={handleClose} open={open}>
            <Stack
                spacing={3}
                sx={[
                    {
                        alignItems: 'center',
                    },
                    ...(Array.isArray(style) ? style : [style]),
                ]}
            >
                {IsTauri ? (
                    <Paper
                        elevation={4}
                        sx={{
                            alignItems: 'center',
                            backgroundColor: '#edf2f6',
                            borderColor: 'mlBlue.main',
                            borderRadius: '50%',
                            borderStyle: 'solid',
                            borderWidth: 2,
                            display: 'flex',
                            height: 60,
                            justifyContent: 'center',
                            width: 60,
                        }}
                    >
                        <QrCodeScannerIcon sx={{ color: 'primary.main', height: 35, width: 35 }} />
                    </Paper>
                ) : null}
                {renderQrContent()}
            </Stack>
        </Modal>
    );
}

const style = {
    left: '50%',
    minWidth: 400,
    position: 'absolute',
    top: '50%',
    transform: 'translate(-50%, -50%)',
    // minHeight: 400,
    bgcolor: 'background.paper',
    borderRadius: 6,
    p: 3,
};

export function DownloadAppPc() {
    return (
        <Stack spacing={2}>
            <Typography
                sx={{
                    fontWeight: 'bold',
                }}
                variant="h5"
            >
                Download on PC
            </Typography>
            <Stack direction="row">
                <Avatar
                    alt="Renki Logo"
                    src={getCDNImagePath('renki/renkilogo.png')}
                    sx={{
                        height: 100,
                        width: 100,
                    }}
                    variant="rounded"
                />
                <Stack
                    sx={{
                        alignItems: 'flex-start',
                        justifyContent: 'center',
                        pl: 2,
                    }}
                >
                    <Typography
                        color="primary"
                        sx={{
                            fontWeight: '500',
                        }}
                        variant="h4"
                    >
                        Renki
                    </Typography>
                    <Typography color="primary">Companion app</Typography>
                </Stack>
            </Stack>
            <Typography>Renki App is available on Microsoft Store.</Typography>
            <Button
                onClick={() => console.log('Download button clicked')}
                sx={{ alignSelf: 'center', borderColor: 'mlBlue.main', color: 'mlBlue.main' }}
                variant="outlined"
            >
                Microsoft Store
            </Button>
        </Stack>
    );
}

interface DownloadAppMobileProps {
    handleDisplayRedirectQr?: () => void;
    update?: boolean;
    url: string;
}

export function DownloadAppMobile({ handleDisplayRedirectQr, url }: Readonly<DownloadAppMobileProps>) {
    return (
        <>
            <Stack
                direction="row"
                spacing={10}
                sx={{
                    px: 4,
                }}
            >
                <Stack
                    spacing={2}
                    sx={{
                        alignItems: 'center',
                    }}
                >
                    <Stack
                        spacing={1}
                        sx={{
                            alignItems: 'center',
                            textAlign: 'center',
                        }}
                    >
                        <Stack
                            sx={{
                                alignItems: 'center',
                            }}
                        >
                            <AppleIcon sx={{ height: 50, width: 50 }} />
                            <Typography variant="h6">App Store</Typography>
                        </Stack>
                        <Typography
                            sx={{
                                fontWeight: 'bold',
                            }}
                            variant="h5"
                        >
                            Scan QR code
                        </Typography>
                        <Typography>Scan QR code with mobile device.</Typography>
                    </Stack>
                    <QRCode fgColor="black" id="qr-image" level="H" size={160} value={url} />
                </Stack>
                <Stack
                    spacing={2}
                    sx={{
                        alignItems: 'center',
                    }}
                >
                    <Stack
                        spacing={1}
                        sx={{
                            alignItems: 'center',
                            textAlign: 'center',
                        }}
                    >
                        <Stack
                            sx={{
                                alignItems: 'center',
                            }}
                        >
                            <AndroidIcon sx={{ color: '#3DDC84', height: 50, width: 50 }} />
                            <Typography variant="h6">Google Play Store</Typography>
                        </Stack>
                        <Typography
                            sx={{
                                fontWeight: 'bold',
                            }}
                            variant="h5"
                        >
                            Scan QR code
                        </Typography>
                        <Typography>Scan QR code with mobile device.</Typography>
                    </Stack>
                    <QRCode fgColor="black" id="qr-image" level="H" size={160} value={url} />
                </Stack>
            </Stack>
            <Divider sx={{ color: 'mlBlue.main', width: '100%' }}>if you already have an App</Divider>
            <Button
                onClick={handleDisplayRedirectQr}
                sx={{ borderColor: 'mlBlue.main', color: 'mlBlue.main' }}
                variant="outlined"
            >
                Get the Code
            </Button>
            <Stack sx={{ width: '75%' }}>
                <Typography
                    sx={{
                        color: 'mlBlue.main',
                    }}
                    variant="h6"
                >
                    Don't want to Download an App right now?
                </Typography>
                <Typography
                    sx={{
                        color: 'mlBlue.main',
                    }}
                    variant="h6"
                >
                    You can try our App-less method.
                </Typography>
            </Stack>
            <Button
                onClick={handleDisplayRedirectQr}
                sx={{ borderColor: 'mlBlue.main', color: 'mlBlue.main' }}
                variant="outlined"
            >
                App-less code
            </Button>
        </>
    );
}

export function QrRedirect({ handleDisplayRedirectQr, update, url }: Readonly<DownloadAppMobileProps>) {
    return (
        <>
            <Stack direction="row" spacing={15}>
                <Stack
                    spacing={2}
                    sx={{
                        alignItems: 'center',
                    }}
                >
                    <Stack
                        spacing={1}
                        sx={{
                            alignItems: 'center',
                            textAlign: 'center',
                        }}
                    >
                        <Stack
                            sx={{
                                alignItems: 'center',
                            }}
                        >
                            <Avatar
                                sx={{ backgroundColor: 'transparent', color: 'primary.main', height: 50, width: 50 }}
                            >
                                {DeviceIcons[2]}
                            </Avatar>
                            <Typography variant="h6">{update ? 'Update' : 'Add'} Handheld Device</Typography>
                        </Stack>
                        <Typography
                            sx={{
                                fontWeight: 'bold',
                            }}
                            variant="h5"
                        >
                            Scan QR code
                        </Typography>
                        <Typography
                            sx={{
                                fontWeight: 'bold',
                            }}
                            variant="h6"
                        >
                            Make sure that you are:
                        </Typography>
                        <List sx={{ listStyleType: 'disc', pl: 2 }}>
                            <ListItem
                                sx={{
                                    display: 'list-item',
                                    p: 0,
                                }}
                            >
                                <Typography>at Renki-Location</Typography>
                            </ListItem>
                            <ListItem sx={{ display: 'list-item', p: 0 }}>
                                <Typography>connected to office Wi-Fi network</Typography>
                            </ListItem>
                        </List>
                    </Stack>
                    <QRCode fgColor="black" id="qr-image" level="H" size={160} value={url} />
                </Stack>
            </Stack>
            <Divider sx={{ color: 'mlBlue.main', width: '100%' }}>if you do not have an App</Divider>
            <Button
                onClick={handleDisplayRedirectQr}
                sx={{ borderColor: 'mlBlue.main', color: 'mlBlue.main' }}
                variant="outlined"
            >
                Get the App
            </Button>
        </>
    );
}
